import { inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Router, NavigationEnd, NavigationSkipped } from '@angular/router';
import { Store } from '@ngrx/store';
import {
  catchError,
  EMPTY,
  filter,
  map,
  mergeMap,
  of,
  pairwise,
  startWith,
  switchMap,
  withLatestFrom,
} from 'rxjs';
import { routerSegments } from '../../consts/routes';
import { AuthActions } from '../state/auth/auth.action-types';
import { OrgUsersActions } from '../state/org-users/org-users.actions';
import { orgUsersFeature } from '../state/org-users/org-users.feature';
import { OrgUsersService } from '../../services/org-users.service';
import { OrgUsersMapResponse } from '../state/org-users/org-users.models';

const isHomeUrl = (url: string): boolean => url.startsWith(`/${routerSegments.home}`);

export const loadOrgUsersOnRequiredDataLoaded = createEffect(
  (actions$ = inject(Actions)) => {
    return actions$.pipe(
      ofType(AuthActions.requiredDataLoadedSuccess),
      map(() => OrgUsersActions.loadOrgUsers({ force: false })),
    );
  },
  { functional: true },
);

export const loadOrgUsersOnHomeEnter = createEffect(
  (router = inject(Router)) => {
    return router.events.pipe(
      filter((event): event is NavigationEnd | NavigationSkipped =>
        event instanceof NavigationEnd || event instanceof NavigationSkipped,
      ),
      map(() => router.url),
      startWith(router.url),
      pairwise(),
      filter(([prev, curr]) => !isHomeUrl(prev) && isHomeUrl(curr)),
      map(() => OrgUsersActions.loadOrgUsers({ force: true })),
    );
  },
  { functional: true },
);

export const loadUsersByIds = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    orgUsersService = inject(OrgUsersService),
  ) => {
    return actions$.pipe(
      ofType(OrgUsersActions.loadUsersByIds),
      withLatestFrom(store.select(orgUsersFeature.selectEntities)),
      map(([{ userIds }, entities]) =>
        Array.from(new Set(userIds.filter((userId) => !!userId && !entities[userId]))),
      ),
      filter((missingIds) => missingIds.length > 0),
      mergeMap((missingIds) =>
        orgUsersService.getUsersByIds(missingIds).pipe(
          map((mapResponse: OrgUsersMapResponse) =>
            OrgUsersActions.loadUsersByIdsSuccess({
              users: Object.values(mapResponse ?? {}),
            }),
          ),
          catchError((error: Error) =>
            of(OrgUsersActions.loadUsersByIdsFail({ error: error.message })),
          ),
        ),
      ),
    );
  },
  { functional: true },
);

export const loadOrgUsers = createEffect(
  (
    actions$ = inject(Actions),
    store = inject(Store),
    orgUsersService = inject(OrgUsersService),
  ) => {
    return actions$.pipe(
      ofType(OrgUsersActions.loadOrgUsers),
      withLatestFrom(
        store.select(orgUsersFeature.selectLoaded),
        store.select(orgUsersFeature.selectTotal),
      ),
      switchMap(([{ force }, loaded, total]) => {
        if (loaded && !force && total > 0) {
          return EMPTY;
        }
        return orgUsersService.getOrgUsersMap().pipe(
          map((mapResponse: OrgUsersMapResponse) =>
            OrgUsersActions.loadOrgUsersSuccess({
              users: Object.values(mapResponse ?? {}),
            }),
          ),
          catchError((error: Error) =>
            of(OrgUsersActions.loadOrgUsersFail({ error: error.message })),
          ),
        );
      }),
    );
  },
  { functional: true },
);
